import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTimezoneContext } from 'src/contexts/SiteTimezoneContext/SiteTimezoneContext';
import {
  AggregatedSaleMetric,
  AggregateMetricsTimePeriod,
  UiCurrency,
} from 'src/WebApiController';

import { useGetAggregatedSaleMetrics } from './hooks/useGetAggregatedSaleMetrics';
import { useGetRecentSalesForEvents } from './hooks/useGetRecentSalesForEvents';
import { generateTimeSlots } from './utils/groupSalesByGranularity';
import { stitchChartData } from './utils/stitchChartData';
import { stitchNonCumulativeChartData } from './utils/stitchNonCumulativeChartData';

export type SaleChartData = {
  dateTime: string;
  totalPnl: number | null;
  totalRevenue: number | null;
  totalQuantity: number | null;
  marketshareQuantity: Record<string, number> | null;
  marketshare: Record<string, number> | null;
};

export type TicketsSoldChartData = {
  dateTime: string;
  totalQuantity: number;
};

type DataInsightsContextType = {
  chartData: SaleChartData[] | null;
  paddedChartData: SaleChartData[] | null;
  marketplaceChartData: SaleChartData[] | null;
  isLoading: boolean;
  isFetching: boolean;
  uiCurrency: UiCurrency;
  targetValue: number | null;
  aggregatedHistoricalData?: AggregatedSaleMetric[] | null;
  setTargetValue: (value: number | null) => void;
  timePeriod?: AggregateMetricsTimePeriod;
  setTimePeriod?: React.Dispatch<
    React.SetStateAction<AggregateMetricsTimePeriod>
  >;
  todayRecentDate: string | null;
  isScrolling: boolean;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
};

const DataInsightsContext = createContext<DataInsightsContextType | undefined>(
  undefined
);

export function DataInsightsProvider({ children }: { children: ReactNode }) {
  const { loginContext } = useAppContext();
  const { timeZone } = useSiteTimezoneContext();
  const { getUiCurrency } = useLocalizationContext();
  const [targetValue, setTargetValue] = useState<number | null>(null);
  const [timePeriod, setTimePeriod] = useState<AggregateMetricsTimePeriod>(
    AggregateMetricsTimePeriod.Today
  );
  const [isScrolling, setIsScrolling] = useState(false);

  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const {
    data: aggregatedHistoricalData,
    isLoading,
    isFetching,
  } = useGetAggregatedSaleMetrics(timePeriod);
  const { pastRecentSales } = useGetRecentSalesForEvents();
  const [stitchedChartData, paddedStitchedData, nonCumulativeData, currDate] =
    useMemo(() => {
      const cumulativeData = stitchChartData(
        aggregatedHistoricalData,
        pastRecentSales,
        timeZone,
        timePeriod,
        true
      );

      const nonCumulativeData = stitchNonCumulativeChartData(
        aggregatedHistoricalData,
        pastRecentSales,
        timeZone,
        timePeriod,
        true
      );

      let currDate = null;
      const paddedStitchedData = cumulativeData ? [...cumulativeData] : [];

      if (
        timePeriod === AggregateMetricsTimePeriod.Today &&
        aggregatedHistoricalData &&
        cumulativeData
      ) {
        currDate =
          aggregatedHistoricalData[aggregatedHistoricalData.length - 1]
            .dateTime;
        const emptyTimeSlots = generateTimeSlots(
          aggregatedHistoricalData[aggregatedHistoricalData.length - 1]
            .dateTime,
          timeZone
        );

        paddedStitchedData.push(...emptyTimeSlots);
      }
      return [cumulativeData, paddedStitchedData, nonCumulativeData, currDate];
    }, [aggregatedHistoricalData, pastRecentSales, timePeriod, timeZone]);

  return (
    <DataInsightsContext.Provider
      value={{
        todayRecentDate: currDate,
        chartData: stitchedChartData,
        paddedChartData: paddedStitchedData,
        marketplaceChartData: nonCumulativeData,
        isLoading,
        isFetching,
        uiCurrency,
        targetValue,
        setTargetValue,
        timePeriod,
        setTimePeriod,
        isScrolling,
        setIsScrolling,
      }}
    >
      {children}
    </DataInsightsContext.Provider>
  );
}

export function useDataInsights() {
  const context = useContext(DataInsightsContext);
  if (!context) {
    throw new Error(
      'useDataInsights must be used within a DataInsightsProvider'
    );
  }
  return context;
}
